import { deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import type { Timestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { trackWrite } from '../utils/syncTracker'

/**
 * Den kørende arbejdstimer — ét dokument pr. bruger: adminTimers/{uid}.
 * Kun starttidspunktet gemmes; den forløbne tid regnes altid ud fra det,
 * så timeren tæller korrekt efter låst skærm, lukket app og genstart.
 */
export interface RunningTimer {
  /** Fast id for denne kørsel — bruges som id på timelinjen, så stop aldrig registreres to gange. */
  timerId: string
  caseId: string
  caseTitle: string
  /** ISO-tid. Servertid, estimeret lokalt indtil serveren har bekræftet. */
  startedAt: string
  status: 'running'
  lastReminderAt: string | null
}

const timerRef = (uid: string) => doc(db, 'adminTimers', uid)

function toIso(value: unknown): string | null {
  const stamp = value as Timestamp | null | undefined
  return stamp && typeof stamp.toDate === 'function' ? stamp.toDate().toISOString() : null
}

export function subscribeTimer(
  uid: string,
  onChange: (timer: RunningTimer | null) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    timerRef(uid),
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange(null)
        return
      }
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      onChange({
        timerId: data.timerId ?? `timer-${snapshot.id}`,
        caseId: data.caseId ?? '',
        caseTitle: data.caseTitle ?? '',
        startedAt: toIso(data.startedAt) ?? new Date().toISOString(),
        status: 'running',
        lastReminderAt: toIso(data.lastReminderAt),
      })
    },
    onError,
  )
}

export function startTimerDoc(uid: string, caseId: string, caseTitle: string): Promise<void> {
  return trackWrite(
    setDoc(timerRef(uid), {
      timerId: `timer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      caseId,
      caseTitle,
      startedAt: serverTimestamp(),
      status: 'running',
      lastReminderAt: null,
    }),
  )
}

export function clearTimerDoc(uid: string): Promise<void> {
  return trackWrite(deleteDoc(timerRef(uid)))
}
